/**
 * Deployment Grid with Drag-and-Drop
 *
 * Enhanced DeploymentGrid component with integrated drag-and-drop functionality
 * for creature placement on the hex grid.
 *
 * Now includes zoom/pan controls (L4-ZOOM-005):
 * - Mouse wheel zoom (toward cursor)
 * - WASD keyboard pan
 * - +/- button controls
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { HexGridRenderer, HighlightState } from './DeploymentGridRenderer';
import type { AxialCoordinate, DeploymentCreature, CreaturePlacement } from '@drawn-of-war/shared';
import styles from './DeploymentGrid.module.css';
import { ZoomControls } from '../ZoomControls';
import {
  createWheelZoomHandler,
  createKeyboardPanHandler,
  createTouchHandler,
  KeyboardPanHandler,
  TouchHandler,
  calculateWheelZoom,
} from '../../services/camera';

export interface DeploymentGridWithDragDropProps {
  /** Canvas width in pixels */
  width?: number;
  /** Canvas height in pixels */
  height?: number;
  /** Hex size (radius) in pixels */
  hexSize?: number;
  /** Show coordinate labels for debugging */
  showCoordinates?: boolean;
  /** Callback when hex is clicked */
  onHexClick?: (hex: AxialCoordinate) => void;
  /** Callback when hex is hovered */
  onHexHover?: (hex: AxialCoordinate | null) => void;
  /** Creature placements to render */
  placements?: CreaturePlacement[];
  /** Opponent creature placements to render (read-only) */
  opponentPlacements?: CreaturePlacement[];
  /** Currently dragging creature (for preview) */
  draggingCreature?: DeploymentCreature | null;
  /** Target hex while dragging */
  dragTargetHex?: AxialCoordinate | null;
  /** Whether target is valid */
  isDragValid?: boolean;
  /** Callback when drop occurs on grid */
  onDrop?: (hex: AxialCoordinate) => void;
  /** Current player ID (for determining opponent team color) */
  currentPlayerId?: 'player1' | 'player2';
  /** Callback when a creature sprite is clicked (for repositioning) */
  onCreatureClick?: (creatureId: string, hex: AxialCoordinate) => void;
  /** Callback when a creature sprite is right-clicked (for removal) */
  onCreatureRightClick?: (creatureId: string, hex: AxialCoordinate) => void;

  // Mobile tap-and-hold removal callbacks
  /** Callback when hold is detected (500ms threshold passed) */
  onRemovalHoldDetected?: (creatureId: string, hex: AxialCoordinate) => void;
  /** Callback for removal progress (0-1, called each frame during removal) */
  onRemovalProgress?: (progress: number) => void;
  /** Callback when removal completes (3 second hold) */
  onRemovalComplete?: (creatureId: string, hex: AxialCoordinate) => void;
  /** Callback when removal is cancelled (user released early or moved) */
  onRemovalCancelled?: () => void;

  /** ID of creature selected for repositioning (renders semi-transparent) */
  repositionCreatureId?: string | null;

  /** Dev callback: renderer instance available for dev tools (e.g. background position editor) */
  onRendererReady?: (renderer: HexGridRenderer | null) => void;

  /** Custom background image URL (overrides parallax terrain background) */
  backgroundImageOverride?: string;
  /** Background center X offset from grid center in world pixels */
  bgOffsetX?: number;
  /** Background center Y offset from grid center in world pixels */
  bgOffsetY?: number;
  /** Background scale as fraction of natural texture size (1.0 = original) */
  bgScale?: number;
  /** Background brightness (0–2, default 1.0) */
  bgBrightness?: number;
  /** Background contrast (0–2, default 1.0) */
  bgContrast?: number;
  /** Background saturation (0–2, default 1.0) */
  bgSaturation?: number;
  /** Background hue rotation in degrees (-180–180, default 0) */
  bgHue?: number;
  /** Grid columns (defaults to 12) */
  gridColumns?: number;
  /** Grid rows (defaults to 8) */
  gridRows?: number;
  /** Deployment zone configuration (default: left-vs-right PvP zones) */
  deploymentZoneConfig?: {
    orientation: 'left_vs_right' | 'top_vs_bottom';
    playerDeployCols?: number;
    player1ColRange?: { min: number; max: number };
    player2ColRange?: { min: number; max: number };
    playerDeployRows?: number;
    enemyDeployRows?: number;
    gridHeight?: number;
    gridWidth?: number;
  };
}

export const DeploymentGridWithDragDrop: React.FC<DeploymentGridWithDragDropProps> = ({
  width = 1200,
  height = 700,
  hexSize = 38,
  showCoordinates = false,
  onHexClick,
  onHexHover,
  placements = [],
  opponentPlacements = [],
  draggingCreature = null,
  dragTargetHex = null,
  isDragValid = false,
  onDrop,
  currentPlayerId,
  onCreatureClick,
  onCreatureRightClick,
  // Mobile removal callbacks
  onRemovalHoldDetected,
  onRemovalProgress,
  onRemovalComplete,
  onRemovalCancelled,
  // Creature selected for repositioning
  repositionCreatureId = null,
  // Dev tools
  onRendererReady,
  // Custom background
  backgroundImageOverride,
  bgOffsetX,
  bgOffsetY,
  bgScale,
  bgBrightness,
  bgContrast,
  bgSaturation,
  bgHue,
  gridColumns,
  gridRows,
  deploymentZoneConfig,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<HexGridRenderer | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentHex, setCurrentHex] = useState<AxialCoordinate | null>(null);
  const [debugMode, setDebugMode] = useState(showCoordinates);

  // Camera control refs
  const keyboardPanRef = useRef<KeyboardPanHandler | null>(null);
  const touchHandlerRef = useRef<TouchHandler | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Refs to always access latest callbacks (prevents stale closure issues with PixiJS renderer)
  const onHexHoverRef = useRef(onHexHover);
  const onHexClickRef = useRef(onHexClick);
  const onCreatureClickRef = useRef(onCreatureClick);
  const onCreatureRightClickRef = useRef(onCreatureRightClick);
  // Mobile removal callback refs
  const onRemovalHoldDetectedRef = useRef(onRemovalHoldDetected);
  const onRemovalProgressRef = useRef(onRemovalProgress);
  const onRemovalCompleteRef = useRef(onRemovalComplete);
  const onRemovalCancelledRef = useRef(onRemovalCancelled);

  // Keep refs updated with latest prop values
  useEffect(() => {
    onHexHoverRef.current = onHexHover;
  }, [onHexHover]);

  useEffect(() => {
    onHexClickRef.current = onHexClick;
  }, [onHexClick]);

  useEffect(() => {
    onCreatureClickRef.current = onCreatureClick;
  }, [onCreatureClick]);

  useEffect(() => {
    onCreatureRightClickRef.current = onCreatureRightClick;
  }, [onCreatureRightClick]);

  // Keep mobile removal refs updated
  useEffect(() => {
    onRemovalHoldDetectedRef.current = onRemovalHoldDetected;
  }, [onRemovalHoldDetected]);

  useEffect(() => {
    onRemovalProgressRef.current = onRemovalProgress;
  }, [onRemovalProgress]);

  useEffect(() => {
    onRemovalCompleteRef.current = onRemovalComplete;
  }, [onRemovalComplete]);

  useEffect(() => {
    onRemovalCancelledRef.current = onRemovalCancelled;
  }, [onRemovalCancelled]);

  // Initialize renderer
  useEffect(() => {
    let renderer: HexGridRenderer | null = null;
    let isCancelled = false;

    const initRenderer = async () => {
      if (!containerRef.current) return;

      // Create renderer
      // Note: All callbacks use refs internally to prevent stale closures
      renderer = new HexGridRenderer({
        canvasWidth: width,
        canvasHeight: height,
        hexGridConfig: {
          width: gridColumns ?? 12,
          height: gridRows ?? 8,
          hexSize: hexSize,
          orientation: 'flat-top',
          projection: 'isometric'
        },
        showCoordinates: debugMode,
        onHexHover: handleHexHover,
        onHexClick: handleHexClickInternal,
        currentPlayerId,
        onCreatureClick: handleCreatureClickInternal,
        onCreatureRightClick: handleCreatureRightClickInternal,
        // Mobile tap-and-hold removal callbacks
        onRemovalHoldDetected: handleRemovalHoldDetectedInternal,
        onRemovalProgress: handleRemovalProgressInternal,
        onRemovalComplete: handleRemovalCompleteInternal,
        onRemovalCancelled: handleRemovalCancelledInternal,
        // Custom background image
        backgroundImageOverride,
        bgOffsetX,
        bgOffsetY,
        bgScale,
        bgBrightness,
        bgContrast,
        bgSaturation,
        bgHue,
        deploymentZoneConfig,
      });

      try {
        // Initialize and get canvas
        const canvas = await renderer.init();

        // Check if effect was cancelled during async init (React 18 strict mode)
        if (isCancelled) {
          console.log('[DeploymentGridWithDragDrop] Init completed but effect was cancelled, skipping mount');
          return;
        }

        // Add canvas to container
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
          containerRef.current.appendChild(canvas);
        }

        rendererRef.current = renderer;
        setIsInitialized(true);
        onRendererReady?.(renderer);
      } catch (error) {
        // Initialization was aborted (likely due to destroy during init)
        console.log('[DeploymentGridWithDragDrop] Renderer initialization aborted:', error);
      }
    };

    initRenderer();

    // Cleanup
    return () => {
      isCancelled = true;
      if (renderer) {
        renderer.destroy();
        rendererRef.current = null;
        setIsInitialized(false);
        onRendererReady?.(null);
      }
    };
  }, [width, height, hexSize, backgroundImageOverride, bgOffsetX, bgOffsetY, bgScale, bgBrightness, bgContrast, bgSaturation, bgHue]); // Reinitialize if dimensions or background change

  // Setup camera controls (zoom/pan) - L4-ZOOM-005
  useEffect(() => {
    if (!rendererRef.current || !isInitialized) return;

    const renderer = rendererRef.current;
    const cameraManager = renderer.getCameraManager();
    const canvas = renderer.getCanvas();

    if (!cameraManager || !canvas) return;

    // Center camera on grid content
    const gridOffset = renderer.getGridOffset();
    const gridBounds = renderer.getGridBounds();

    const contentBounds = {
      x: gridOffset.x + gridBounds.minX,
      y: gridOffset.y + gridBounds.minY,
      width: gridBounds.width,
      height: gridBounds.height
    };

    // Use 5% margin and slight vertical bias to show more battlefield
    cameraManager.fitToContent(contentBounds, width, height, 0.05, 0.05);

    // Setup wheel zoom handler
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();

      // Get cursor position relative to canvas
      const rect = canvas.getBoundingClientRect();
      const cursorPos = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };

      // Calculate new zoom level
      const currentZoom = cameraManager.getZoom();
      const config = cameraManager.getConfig();
      const targetZoom = calculateWheelZoom(event.deltaY, currentZoom, config.wheelZoomFactor);

      // Apply zoom at cursor position
      renderer.zoomAtPoint(targetZoom, cursorPos);
    };

    // Setup WASD keyboard pan handler
    const panHandler = createKeyboardPanHandler(cameraManager);
    keyboardPanRef.current = panHandler;

    // Setup touch handler for mobile (pinch-to-zoom + drag-to-pan with threshold)
    // Drag threshold allows quick taps to pass through to PixiJS for hex clicks,
    // while drags beyond 10px threshold will pan the camera
    const touchHandler = createTouchHandler(cameraManager, canvas, {
      dragThreshold: 10, // 10px threshold before pan starts
      enablePan: true,
    });
    touchHandlerRef.current = touchHandler;

    // Animation loop for continuous pan updates
    const updateLoop = () => {
      if (keyboardPanRef.current) {
        keyboardPanRef.current.update();
        cameraManager.applyTransform();
      }
      animationFrameRef.current = requestAnimationFrame(updateLoop);
    };

    // Add event listeners
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    canvas.addEventListener('touchstart', touchHandler.handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', touchHandler.handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', touchHandler.handleTouchEnd);
    window.addEventListener('keydown', panHandler.handleKeyDown);
    window.addEventListener('keyup', panHandler.handleKeyUp);

    // Start update loop
    animationFrameRef.current = requestAnimationFrame(updateLoop);

    console.log('[DeploymentGridWithDragDrop] Camera controls initialized (wheel, touch, keyboard)');

    // Cleanup
    return () => {
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('touchstart', touchHandler.handleTouchStart);
      canvas.removeEventListener('touchmove', touchHandler.handleTouchMove);
      canvas.removeEventListener('touchend', touchHandler.handleTouchEnd);
      window.removeEventListener('keydown', panHandler.handleKeyDown);
      window.removeEventListener('keyup', panHandler.handleKeyUp);

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      if (keyboardPanRef.current) {
        keyboardPanRef.current.destroy();
        keyboardPanRef.current = null;
      }

      if (touchHandlerRef.current) {
        touchHandlerRef.current.destroy();
        touchHandlerRef.current = null;
      }
    };
  }, [isInitialized]);

  // Zoom button handlers
  const handleZoomIn = useCallback(() => {
    if (rendererRef.current) {
      rendererRef.current.zoomIn();
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (rendererRef.current) {
      rendererRef.current.zoomOut();
    }
  }, []);

  // Handle hex hover - uses ref to always call latest callback (prevents stale closure)
  const handleHexHover = useCallback((hex: AxialCoordinate | null) => {
    setCurrentHex(hex);
    onHexHoverRef.current?.(hex);
  }, []); // Empty deps - always uses current ref value

  // Handle hex click - uses ref to always call latest callback (prevents stale closure)
  const handleHexClickInternal = useCallback((hex: AxialCoordinate) => {
    onHexClickRef.current?.(hex);
  }, []); // Empty deps - always uses current ref value

  // Handle creature click - uses ref to always call latest callback (prevents stale closure)
  const handleCreatureClickInternal = useCallback((creatureId: string, hex: AxialCoordinate) => {
    onCreatureClickRef.current?.(creatureId, hex);
  }, []); // Empty deps - always uses current ref value

  // Handle creature right-click - uses ref to always call latest callback (prevents stale closure)
  const handleCreatureRightClickInternal = useCallback((creatureId: string, hex: AxialCoordinate) => {
    onCreatureRightClickRef.current?.(creatureId, hex);
  }, []); // Empty deps - always uses current ref value

  // Mobile removal handlers - use refs to always call latest callbacks
  const handleRemovalHoldDetectedInternal = useCallback((creatureId: string, hex: AxialCoordinate) => {
    onRemovalHoldDetectedRef.current?.(creatureId, hex);
  }, []);

  const handleRemovalProgressInternal = useCallback((progress: number) => {
    onRemovalProgressRef.current?.(progress);
  }, []);

  const handleRemovalCompleteInternal = useCallback((creatureId: string, hex: AxialCoordinate) => {
    onRemovalCompleteRef.current?.(creatureId, hex);
  }, []);

  const handleRemovalCancelledInternal = useCallback(() => {
    onRemovalCancelledRef.current?.();
  }, []);

  // Render creature placements
  useEffect(() => {
    if (!rendererRef.current || !isInitialized) return;

    // Clear all creatures first
    rendererRef.current.clearAllCreatures();

    // Render opponent placements first (so they appear behind)
    // Note: opponent creatures don't get IDs passed since they're not interactive
    opponentPlacements.forEach(placement => {
      rendererRef.current!.renderCreature(
        placement.hex,
        placement.creature.name,
        placement.creature.playerId,
        placement.creature, // Pass entire creature object (contains battlefieldDirectionalViews with idleFrames)
        0.5, // 50% opacity for opponent creatures
        placement.creature.facing // Pass facing direction
        // No creatureId - opponent creatures are not interactive
      );
    });

    // Render own placements on top with creature IDs for interactivity
    placements.forEach(placement => {
      // Creature selected for repositioning renders semi-transparent
      const isSelectedForReposition = repositionCreatureId === placement.creature.id;
      const opacity = isSelectedForReposition ? 0.5 : 1.0;

      rendererRef.current!.renderCreature(
        placement.hex,
        placement.creature.name,
        placement.creature.playerId,
        placement.creature, // Pass entire creature object (contains battlefieldDirectionalViews with idleFrames)
        opacity, // Semi-transparent if selected for repositioning, full opacity otherwise
        placement.creature.facing, // Pass facing direction
        placement.creature.id // Pass creature ID for click-to-reposition
      );
    });
  }, [placements, opponentPlacements, isInitialized, repositionCreatureId]);

  // Handle drag preview
  useEffect(() => {
    if (!rendererRef.current || !isInitialized) return;

    // Clear existing preview
    rendererRef.current.clearDragPreview();

    // Show preview if dragging
    if (draggingCreature && dragTargetHex) {
      rendererRef.current.renderDragPreview(
        dragTargetHex,
        draggingCreature.name,
        draggingCreature.playerId,
        draggingCreature, // Pass entire creature object (contains battlefieldDirectionalViews with idleFrames)
        draggingCreature.facing // Pass facing direction (will update based on movement)
      );

      // Update highlight based on validity
      rendererRef.current.updateHighlight(
        dragTargetHex,
        isDragValid ? HighlightState.VALID : HighlightState.INVALID
      );
    } else {
      rendererRef.current.clearHighlights();
    }
  }, [draggingCreature, dragTargetHex, isDragValid, isInitialized]);

  // Handle drag over grid - CRITICAL: Update hex hover during HTML5 drag
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    // Get mouse position relative to canvas
    if (rendererRef.current && containerRef.current) {
      const canvas = containerRef.current.querySelector('canvas');
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        // Get hex at mouse position
        const hex = rendererRef.current.getHexAtPixel(x, y);

        console.log('[DragOver] Mouse at pixel:', { x, y }, 'Hex:', hex);

        // Update current hex (triggers onHexHover callback)
        if (hex) {
          handleHexHover(hex);
        } else {
          handleHexHover(null);
        }
      }
    }
  }, [handleHexHover]);

  // Handle drop on grid
  const handleDropOnGrid = useCallback((event: React.DragEvent) => {
    event.preventDefault();

    if (!rendererRef.current || !currentHex) return;

    onDrop?.(currentHex);
  }, [currentHex, onDrop]);

  // Toggle debug mode with 'G' key
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'g' || e.key === 'G') {
        setDebugMode(prev => {
          const newDebugMode = !prev;
          if (rendererRef.current) {
            rendererRef.current.toggleCoordinates();
          }
          return newDebugMode;
        });
      }

      // ESC to cancel drag (handled by parent component)
      if (e.key === 'Escape' && draggingCreature) {
        // Parent should handle this via cancelDrag
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [draggingCreature]);

  return (
    <div className={styles.container}>
      <div
        ref={containerRef}
        className={styles.canvasContainer}
        onDragOver={handleDragOver}
        onDrop={handleDropOnGrid}
      />
      {/* Zoom controls overlay - L4-ZOOM-008 */}
      {isInitialized && (
        <ZoomControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
        />
      )}
    </div>
  );
};

export default DeploymentGridWithDragDrop;
